import { type RefObject, type SyntheticEvent, useLayoutEffect, useRef } from 'react';

export interface ModalDialog {
  readonly ref: RefObject<HTMLDialogElement | null>;
  /** `<dialog onCancel>` — Escape 로 닫힐 때 브라우저 대신 호출부 상태로 닫는다. */
  readonly onCancel: (event: SyntheticEvent<HTMLDialogElement>) => void;
  /**
   * `<dialog onClose>` — 브라우저가 cancel 없이 직접 닫은 경우(Escape 를 거듭 누르면 막은 cancel 다음에 그대로 닫힌다)를
   * 화면 상태와 맞춘다. 닫아도 되면 닫기 처리를 부르고, 처리 중이면 다시 모달로 띄운다.
   */
  readonly onClose: (event: SyntheticEvent<HTMLDialogElement>) => void;
}

/**
 * 네이티브 `<dialog>` 를 모달로 띄운다(web `useModalDialog` 와 같은 동작) — 브라우저가 포커스를 대화상자 안으로 옮기고 바깥을 inert 로 만든다.
 * Escape(cancel)는 기본 닫힘을 막고 `dismissible` 일 때만 `onDismiss` 를 부른다(처리 중에는 닫지 않는다).
 * 닫히면(언마운트) 모달을 닫고, 열기 직전에 포커스가 있던 요소(여는 버튼)로 포커스를 돌려준다 — 렌더가 끝난 뒤(마이크로태스크)에
 * 돌려줘 같은 렌더에서 다시 활성화되는 버튼도 받는다. 마운트가 곧 열림이다 — 호출부는 열려 있을 때만 그린다.
 */
export const useModalDialog = (onDismiss: () => void, dismissible: boolean): ModalDialog => {
  const ref = useRef<HTMLDialogElement | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node.showModal();
    return () => {
      node.close();
      if (opener)
        queueMicrotask(() => {
          if (opener.isConnected) opener.focus();
        });
    };
  }, []);

  return {
    ref,
    onCancel: (event) => {
      event.preventDefault();
      if (dismissible) onDismiss();
    },
    onClose: () => {
      const node = ref.current;
      if (!node || node.open) return;
      if (dismissible) onDismiss();
      else node.showModal();
    },
  };
};
